import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cx } from './cx';

export type ButtonVariant = 'primary' | 'dark' | 'ghost' | 'danger-ghost' | 'link' | 'plain';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';
export type ButtonShape = 'pill' | 'form';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  loading?: boolean;
  /** Adds the primary CTA glow used on auth and Song Studio. */
  glow?: boolean;
  icon?: ReactNode;
  block?: boolean;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-acc text-white hover:bg-acc-h border border-transparent font-semibold',
  dark: 'bg-t1 text-bg hover:opacity-[.88] border border-transparent font-semibold',
  ghost: 'bg-fill text-t1 border border-line2 hover:bg-fill2 font-medium',
  'danger-ghost': 'bg-fill text-acc border border-line2 hover:border-[rgba(208,48,69,.4)] font-medium',
  link: 'bg-transparent text-link hover:text-acc border-0 !px-0 !h-auto font-normal',
  plain: 'bg-transparent text-t3 hover:text-t1 border-0 font-normal',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-[13px] text-[12.5px] gap-1.5',
  md: 'h-10 px-[18px] text-[14px] gap-2',
  lg: 'h-[46px] px-5 text-[14.5px] gap-2',
  xl: 'h-[50px] px-6 text-[15px] gap-2.5',
};

/**
 * The one button. Keeps its width while `loading` by layering the spinner over
 * the (invisible) label rather than swapping the label out.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      shape = 'pill',
      loading = false,
      glow = false,
      icon,
      block = false,
      className,
      children,
      disabled,
      type = 'button',
      ...rest
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        'wp-press relative inline-flex items-center justify-center whitespace-nowrap cursor-pointer select-none',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        shape === 'pill' ? 'rounded-full' : 'rounded-[13px]',
        glow && 'shadow-cta',
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      <span className={cx('inline-flex items-center justify-center gap-[inherit]', loading && 'invisible')}>
        {icon}
        {children}
      </span>
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
          <Spinner size={16} tone={variant === 'primary' ? 'onAccent' : variant === 'dark' ? 'onDark' : 'accent'} />
        </span>
      )}
    </button>
  ),
);
Button.displayName = 'Button';

export type SpinnerTone = 'accent' | 'info' | 'onAccent' | 'onDark';

export function Spinner({ size = 16, tone = 'accent', className }: { size?: number; tone?: SpinnerTone; className?: string }) {
  const style: React.CSSProperties = { width: size, height: size };
  if (tone === 'info') style.borderTopColor = 'var(--info)';
  if (tone === 'onAccent') {
    style.borderColor = 'rgba(255,255,255,.4)';
    style.borderTopColor = '#fff';
  }
  if (tone === 'onDark') {
    style.borderColor = 'color-mix(in srgb, var(--bg) 40%, transparent)';
    style.borderTopColor = 'var(--bg)';
  }
  return <span className={cx('wp-spinner', className)} style={style} aria-hidden="true" />;
}

/** Round icon-only button (header actions, row actions, modal close). */
export const IconButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { size?: number; tone?: 'fill' | 'outline' | 'bare' | 'danger'; label: string }
>(({ size = 36, tone = 'fill', label, className, children, type = 'button', style, ...rest }, ref) => (
  <button
    ref={ref}
    type={type}
    aria-label={label}
    title={rest.title ?? label}
    className={cx(
      'wp-press inline-flex items-center justify-center rounded-full shrink-0 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed',
      tone === 'fill' && 'bg-fill2 text-t3 hover:text-t1 border-0',
      tone === 'outline' && 'bg-fill text-t3 hover:text-t1 border border-line2',
      tone === 'bare' && 'bg-transparent text-t4 hover:bg-fill2 hover:text-t1 border-0',
      tone === 'danger' && 'bg-transparent text-t4 hover:bg-acc-bg hover:text-acc border-0',
      className,
    )}
    style={{ width: size, height: size, ...style }}
    {...rest}
  >
    {children}
  </button>
));
IconButton.displayName = 'IconButton';
